export type TimelineMode = 'ripple' | 'preserve_positions'

export interface TimelineClipLike {
  _id: string
  orderIndex?: number
  duration: number
  trimStart?: number
  trimEnd?: number
  speed?: number
  transition?: { duration: number } | null
  timelineStartTime?: number | null
}

export interface ResolvedTimelineEntry<TClip extends TimelineClipLike = TimelineClipLike> {
  clipIndex: number
  clip: TClip
  globalStart: number
  globalEnd: number
  videoStartOffset: number
  effectiveDuration: number
}

export interface ResolvedTimelineModel<TClip extends TimelineClipLike = TimelineClipLike> {
  entries: ResolvedTimelineEntry<TClip>[]
  totalDuration: number
}

export function resolveTimelineMode(mode?: TimelineMode | null): TimelineMode {
  return mode === 'preserve_positions' ? 'preserve_positions' : 'ripple'
}

export function getEffectiveDuration<TClip extends Pick<TimelineClipLike, 'duration' | 'trimStart' | 'trimEnd' | 'speed'>>(clip: TClip) {
  return (clip.duration - (clip.trimStart || 0) - (clip.trimEnd || 0)) / (clip.speed || 1)
}

function sortTimelineClips<TClip extends TimelineClipLike>(clips: TClip[]) {
  return [...clips].sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
}

export function buildResolvedTimeline<TClip extends TimelineClipLike>(
  clips: TClip[],
  options?: { mode?: TimelineMode | null }
): ResolvedTimelineModel<TClip> {
  if (!clips || !Array.isArray(clips)) return { entries: [], totalDuration: 0 }

  const mode = resolveTimelineMode(options?.mode)
  const sortedClips = sortTimelineClips(clips)
  const entries: ResolvedTimelineEntry<TClip>[] = []

  if (mode === 'preserve_positions') {
    let fallbackTime = 0
    let totalDuration = 0

    for (let i = 0; i < sortedClips.length; i++) {
      const clip = sortedClips[i]
      if (!clip) continue

      const effectiveDuration = getEffectiveDuration(clip) || 5
      const globalStart = typeof clip.timelineStartTime === 'number'
        ? Math.max(0, clip.timelineStartTime)
        : fallbackTime
      const globalEnd = globalStart + effectiveDuration

      entries.push({
        clipIndex: i,
        clip,
        globalStart,
        globalEnd,
        videoStartOffset: clip.trimStart || 0,
        effectiveDuration,
      })

      fallbackTime = Math.max(fallbackTime, globalEnd)
      totalDuration = Math.max(totalDuration, globalEnd)
    }

    return { entries, totalDuration }
  }

  let globalTime = 0

  for (let i = 0; i < sortedClips.length; i++) {
    const clip = sortedClips[i]
    if (!clip) continue

    const effectiveDuration = getEffectiveDuration(clip) || 5
    const entry: ResolvedTimelineEntry<TClip> = {
      clipIndex: i,
      clip,
      globalStart: globalTime,
      globalEnd: globalTime + effectiveDuration,
      videoStartOffset: clip.trimStart || 0,
      effectiveDuration,
    }
    entries.push(entry)

    globalTime += effectiveDuration

    const nextClip = sortedClips[i + 1]
    if (nextClip?.transition) {
      const overlap = Math.min(nextClip.transition.duration, effectiveDuration * 0.5)
      globalTime -= overlap
    }
  }

  return { entries, totalDuration: globalTime }
}

export function getClipTiming<TClip extends TimelineClipLike>(
  clips: TClip[],
  clipId: string,
  options?: { mode?: TimelineMode | null }
) {
  return buildResolvedTimeline(clips, options).entries.find((entry) => entry.clip._id === clipId) || null
}

export function getTimelineSpan<TClip extends TimelineClipLike>(
  clips: TClip[],
  options?: { mode?: TimelineMode | null }
) {
  const entries = buildResolvedTimeline(clips, options).entries
  if (entries.length === 0) return 0
  const start = Math.min(...entries.map((entry) => entry.globalStart))
  const end = Math.max(...entries.map((entry) => entry.globalEnd))
  return Math.max(0, end - start)
}

export function captureTimelineStartTimes<TClip extends TimelineClipLike>(
  clips: TClip[],
  options?: { mode?: TimelineMode | null }
) {
  return buildResolvedTimeline(clips, options).entries.map((entry) => ({
    clipId: entry.clip._id,
    startTime: entry.globalStart,
  }))
}
